import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useFormatDate } from '@/hooks/use-format-date';
import { cn } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';

interface InputDateProps<T extends FieldValues> {
  field: ControllerRenderProps<T, Path<T>>;
  label: string;
  placeholder?: string;
  disabled?: (date: Date) => boolean;
  clearable?: boolean;
  description?: ReactNode;
}

export function InputDate<T extends FieldValues>({
  field,
  label,
  placeholder = 'Select a date',
  disabled,
  clearable = false,
  description,
}: InputDateProps<T>) {
  const { formatWithPreset } = useFormatDate();
  const [open, setOpen] = useState(false);

  return (
    <FormItem className="flex flex-col">
      <FormLabel>{label}</FormLabel>
      <Popover modal={true} open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <FormControl>
            <Button
              variant="outline"
              className={cn(
                'w-full pl-3 text-left font-normal',
                !field.value && 'text-muted-foreground'
              )}
            >
              {field.value ? (
                formatWithPreset(field.value, 'day_month_year')
              ) : (
                <span>{placeholder}</span>
              )}
              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
            </Button>
          </FormControl>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={field.value || undefined}
            onSelect={(date) => {
              if (
                clearable &&
                date &&
                field.value &&
                date.getTime() === field.value.getTime()
              ) {
                field.onChange(null);
              } else {
                field.onChange(date);
                setOpen(false);
              }
            }}
            disabled={disabled}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {description && (
        <div className="mt-2 text-sm text-muted-foreground">{description}</div>
      )}
      <FormMessage />
    </FormItem>
  );
}
